"use client";

import CircleLoader from "@/components/distribution-plan-tool/common/CircleLoader";
import {
  CreateDropType,
  CreateDropViewType,
} from "@/components/drops/create/types";
import type { CreateDropContentHandles } from "@/components/drops/create/utils/CreateDropContent";
import CreateDropContent from "@/components/drops/create/utils/CreateDropContent";
import CreateDropSelectedFileIcon from "@/components/drops/create/utils/file/CreateDropSelectedFileIcon";
import CreateDropSelectedFilePreview from "@/components/drops/create/utils/file/CreateDropSelectedFilePreview";
import type {
  CreateDropConfig,
  DropMetadata,
  MentionedUser,
  MentionedWave,
  ReferencedNft,
} from "@/entities/IDrop";
import type { ApiWaveParticipationRequirement } from "@/generated/models/ApiWaveParticipationRequirement";
import type { ApiWaveRequiredMetadata } from "@/generated/models/ApiWaveRequiredMetadata";
import { assertUnreachable } from "@/helpers/AllowlistToolHelpers";
import type { EditorState } from "lexical";
import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import CreateDropFullMobileMetadata from "./CreateDropFullMobileMetadata";
import CreateDropFullMobileWrapper from "./CreateDropFullMobileWrapper";

enum TITLE_STATE {
  BUTTON = "BUTTON",
  INPUT = "INPUT",
}

export interface CreateDropFullMobileHandles {
  clearEditorState: () => void;
}

interface CreateDropFullMobileProps {
  readonly title: string | null;
  readonly editorState: EditorState | null;
  readonly metadata: DropMetadata[];
  readonly files: File[];
  readonly waveId: string | null;
  readonly canSubmit: boolean;
  readonly canAddPart: boolean;
  readonly type: CreateDropType;
  readonly loading: boolean;
  readonly showSubmit: boolean;
  readonly submitOnEnter?: boolean | undefined;
  readonly drop: CreateDropConfig | null;
  readonly missingMedia: ApiWaveParticipationRequirement[];
  readonly missingMetadata: ApiWaveRequiredMetadata[];
  readonly children: React.ReactNode;
  readonly onEditorState: (editorState: EditorState | null) => void;
  readonly onUploadEditorStateChange: (editorState: EditorState) => void;
  readonly onMetadataEdit: (param: DropMetadata) => void;
  readonly onMetadataRemove: (key: string) => void;
  readonly onMentionedUser: (
    newUser: Omit<MentionedUser, "current_handle">
  ) => void;
  readonly onMentionedWave: (newWave: MentionedWave) => void;
  readonly onReferencedNft: (newNft: ReferencedNft) => void;
  readonly onTitle: (newV: string | null) => void;
  readonly setFiles: (files: File[]) => void;
  readonly onFileRemove: (file: File) => void;
  readonly onViewChange: (newV: CreateDropViewType) => void;
  readonly onDrop: () => void;
  readonly onDropPart: () => void;
}

const CreateDropFullMobile = forwardRef<
  CreateDropFullMobileHandles,
  CreateDropFullMobileProps
>(
  (
    {
      title,
      editorState,
      metadata,
      files,
      waveId,
      canSubmit,
      canAddPart,
      type,
      loading,
      showSubmit,
      submitOnEnter = true,
      drop,
      missingMedia,
      missingMetadata,
      children,
      onEditorState,
      onUploadEditorStateChange,
      onMetadataEdit,
      onMetadataRemove,
      onMentionedUser,
      onMentionedWave,
      onReferencedNft,
      onTitle,
      onFileRemove,
      setFiles,
      onViewChange,
      onDrop,
      onDropPart,
    },
    ref
  ) => {
    const onViewClick = () => {
      if (!loading) {
        onViewChange(CreateDropViewType.COMPACT);
      }
    };
    const [isOpen, setIsOpen] = useState(true);

    const onClose = () => {
      if (!loading) {
        setIsOpen(false);
      }
    };

    const [titleState, setTitleState] = useState<TITLE_STATE>(
      title?.length ? TITLE_STATE.INPUT : TITLE_STATE.BUTTON
    );

    const getSubmitText = () => {
      switch (type) {
        case CreateDropType.DROP:
          return "Drop";
        case CreateDropType.QUOTE:
          return "Quote";
        default:
          assertUnreachable(type);
          return "";
      }
    };

    const editorRef = useRef<CreateDropContentHandles | null>(null);
    const clearEditorState = () => editorRef.current?.clearEditorState();
    useImperativeHandle(ref, () => ({
      clearEditorState,
    }));

    return (
      <CreateDropFullMobileWrapper
        isOpen={isOpen}
        type={type}
        onClose={onClose}
        onViewClick={onViewClick}
      >
        <div className="tw-relative tw-flex-1 tw-space-y-4 tw-divide-x-0 tw-divide-y tw-divide-solid tw-divide-iron-800">
          {children}
          <div className="tw-relative tw-space-y-4 tw-px-4 sm:tw-px-6">
            <div className="-tw-mb-2 tw-flex tw-justify-end">
              {titleState === TITLE_STATE.BUTTON && (
                <button
                  onClick={() => {
                    if (!loading) {
                      setTitleState(TITLE_STATE.INPUT);
                    }
                  }}
                  type="button"
                  disabled={loading}
                  className="tw-inline-flex tw-items-center tw-rounded-lg tw-border-0 tw-bg-iron-800 tw-px-2.5 tw-py-2 tw-text-xs tw-font-semibold tw-text-iron-300 tw-ring-1 tw-ring-inset tw-ring-iron-700 tw-transition tw-duration-300 tw-ease-out hover:tw-bg-iron-700 focus:tw-z-10 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-inset focus:tw-ring-primary-400"
                >
                  <svg
                    className="-tw-ml-1 tw-mr-1 tw-h-4 tw-w-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 5V19M5 12H19"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span>Add title</span>
                </button>
              )}
            </div>
            {titleState === TITLE_STATE.INPUT && (
              <input
                type="text"
                placeholder="Drop title"
                value={title ?? ""}
                onChange={(e) => {
                  if (!loading) {
                    onTitle(e.target.value);
                  }
                }}
                disabled={loading}
                maxLength={250}
                className="tw-form-input tw-block tw-w-full tw-appearance-none tw-rounded-lg tw-border-0 tw-bg-iron-800 tw-py-2.5 tw-pr-3 tw-text-md tw-font-normal tw-leading-6 tw-text-iron-50 tw-caret-primary-400 tw-shadow-sm tw-ring-1 tw-ring-inset tw-ring-iron-800 tw-transition tw-duration-300 tw-ease-out placeholder:tw-text-iron-400 hover:tw-ring-iron-700 focus:tw-bg-iron-900 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-inset focus:tw-ring-primary-400"
              />
            )}
            <CreateDropContent
              ref={editorRef}
              viewType={CreateDropViewType.FULL}
              editorState={editorState}
              type={type}
              waveId={waveId}
              drop={drop}
              loading={loading}
              submitOnEnter={submitOnEnter}
              canAddPart={canAddPart}
              canSubmit={canSubmit}
              missingMedia={missingMedia}
              missingMetadata={missingMetadata}
              onEditorState={onEditorState}
              onUploadEditorStateChange={onUploadEditorStateChange}
              onDrop={onDrop}
              onMentionedUser={onMentionedUser}
              onMentionedWave={onMentionedWave}
              onReferencedNft={onReferencedNft}
              onViewClick={onViewClick}
              setFiles={setFiles}
              onDropPart={onDropPart}
            />
            {files.map((file, i) => (
              <div className="tw-mt-3" key={`full-mobile-file-${i}`}>
                <div className="tw-w-full">
                  <div className="tw-flex tw-items-center tw-justify-between tw-gap-x-1 tw-rounded-lg tw-bg-iron-900 tw-px-4 tw-py-2 tw-ring-1 tw-ring-inset tw-ring-iron-650 tw-transition tw-duration-300 tw-ease-out hover:tw-ring-iron-600">
                    <div className="tw-flex tw-items-center tw-gap-x-3 tw-truncate">
                      <CreateDropSelectedFileIcon file={file} />
                      <p className="tw-mb-0 tw-truncate tw-text-sm tw-font-medium tw-text-iron-50">
                        {file.name}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        if (!loading) {
                          onFileRemove(file);
                        }
                      }}
                      type="button"
                      aria-label="Remove file"
                      disabled={loading}
                      className="-tw-mb-0.5 tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-transparent hover:tw-bg-iron-800"
                    >
                      <svg
                        className="tw-h-5 tw-w-5 tw-flex-shrink-0 tw-text-red"
                        viewBox="0 0 24 24"
                        fill="none"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M18 6L6 18M6 6L18 18"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                  <CreateDropSelectedFilePreview file={file} />
                </div>
              </div>
            ))}
            <CreateDropFullMobileMetadata
              metadata={metadata}
              onMetadataEdit={onMetadataEdit}
              onMetadataRemove={onMetadataRemove}
              disabled={loading}
            />
          </div>
          {showSubmit && (
            <div className="tw-px-4 tw-pt-4 sm:tw-px-6">
              <div className="tw-flex tw-gap-x-3">
                <button
                  type="button"
                  disabled={!canSubmit || loading}
                  onClick={onDrop}
                  className={`${
                    !canSubmit
                      ? "tw-text-iron-200 tw-opacity-50"
                      : "tw-text-white hover:tw-bg-primary-600 hover:tw-ring-primary-600"
                  } tw-relative tw-inline-flex tw-w-full tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-bg-primary-500 tw-px-4 tw-py-3 tw-text-sm tw-font-semibold tw-shadow-sm tw-ring-1 tw-ring-inset tw-ring-primary-500 tw-transition tw-duration-300 tw-ease-out focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-inset`}
                >
                  <div className={loading ? "tw-opacity-0" : ""}>
                    {getSubmitText()}
                  </div>
                  {loading && (
                    <div className="tw-absolute">
                      <CircleLoader />
                    </div>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </CreateDropFullMobileWrapper>
    );
  }
);

CreateDropFullMobile.displayName = "CreateDropFullMobile";
export default CreateDropFullMobile;
